const {
  breadCrumbList,
  eventLocation,
  eventSchemaAttendanceMode,
  eventSchemaStatus,
  listingAddress,
  listingStatusGroup,
  propertyPrice,
  listingPhotos,
  EMPTY_VALUE,
  ADC_DEFAULT_VALUE,
  ADC_URL,
  ADC_POSTAL_ADDRESS_SCHEMA,
  listingPageUrl,
  getValidKeywords,
  DEFAULT_ADC_KEYWORDS,
  DEFAULT_ADC_QA,
} = require('./schemaHelpers')
const { enums } = require('@adc/sdk')
const { get, isNil, isArray, union } = require('lodash')

/**
 * @description standardSchemas includes JSON object for linking data. For search engine optimization (SEO), Linked data allows Google Search to understand the content of the page.
 * Each schema expects similar fields relative to the listingcard schema.
 */

/**
 * @name breadCrumbSchema
 * @description Below are the graphQL fields associated to the breadCrumbSchema properties
 * @param {*} listing
 * @field {string} primary_property.summary.address.country_primary_subdivision
 * @field {string} primary_property.summary.address.country_secondary_subdivision
 * @field {string} primary_property.summary.street_description
 * @field {string} listing_page_path
 */
const breadCrumbSchema = listing => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbList(listing).map(({ label, url }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@id': `${ADC_URL}${url}`,
        name: label,
      },
    })),
  }
}
/**
 * @name eventSchema
 * @param {*} listing
 * @description Below are the graphQL fields associated to the eventSchema properties
 * @field {string} primary_property.summary.formatted_address
 * @field {array.<string>} photos.file_descriptor.urls.cdn
 * @field {string} listing_status_group
 * @field {string} listing_summary.is_remote_bid_enabled
 * @field {string} listing_remarks.mls_comment
 * @field {string} venue.event.event_type
 * @field {string} venue.venue_url
 * @field {string} venue.venue_name
 * @field {string} venue.venue_title
 * @field {string} venue.event.description
 * @field {string} venue.venue_schedule.bid_start_local_date
 * @field {string} venue.venue_address.street_unit
 * @field {string} venue.venue_address.street_number
 * @field {string} venue.venue_address.street_name
 * @field {string} venue.venue_address.municipality
 * @field {string} venue.venue_address.country_primary_subdivision
 * @field {string} venue.venue_address.postal_code
 * @field {string} venue.venue_address.country_code
 */
const eventSchema = (listing, defaultImage = '', keywords = []) => {
  const { streetAddress, formattedAddress, state, county, city } =
    listingAddress(listing)
  const eventStartDate =
    listingStatusGroup(listing) === enums.ListingStatusGroup.Inactive
      ? new Date().toISOString().substring(0, 10)
      : get(listing, 'venue.venue_schedule.bid_start_local_date') ||
        new Date().toISOString().substring(0, 10)
  const eventEndDate =
    listingStatusGroup(listing) === enums.ListingStatusGroup.Inactive
      ? null
      : get(listing, 'venue.venue_schedule.initial_bidding_end_local_date') ||
        null
  const descriptionTitle =
    get(listing, 'venue.venue_title') || ADC_DEFAULT_VALUE
  const descriptionComment = get(listing, 'listing_remarks.mls_comment') || ''
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: `Auction of ${streetAddress} - ${descriptionTitle}`,
    description: `Auction of ${formattedAddress}. ${descriptionComment}`.trim(),
    image: listingPhotos(listing, defaultImage),
    startDate: eventStartDate,
    ...(!isNil(eventEndDate) && {
      endDate: eventEndDate,
    }),
    eventStatus: eventSchemaStatus(listing),
    eventAttendanceMode: eventSchemaAttendanceMode(listing),
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords), [
      state,
      county,
      city,
      streetAddress,
      formattedAddress,
      get(listing, 'venue.venue_title') || ADC_DEFAULT_VALUE,
    ]),
    location: eventLocation(listing),
    organizer: {
      '@type': 'Organization',
      '@id': `${ADC_URL}/#organization`,
      name: ADC_DEFAULT_VALUE,
      url: ADC_URL,
    },
    performer: {
      '@type': 'PerformingGroup',
      name: get(listing, 'venue.venue_title') || ADC_DEFAULT_VALUE,
    },
  }
}
/**
 * @name productSchema
 * @param {*} listing
 * @description Below are the graphQL fields associated to the productSchema properties
 * @field {string} primary_property.summary.formatted_address
 * @field {string} primary_property.summary.valuation'
 * @field {string} primary_photo
 * @field {string} property_details.property_description
 * @field {string} listing_page_path
 * @field {string} auction.starting_bid
 * @field {string} listing_status_group
 */
const productSchema = (listing, defaultImage) => {
  const { formattedAddress } = listingAddress(listing)
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: isArray(formattedAddress)
      ? formattedAddress.join(' ')
      : formattedAddress,
    image: listingPhotos(listing, defaultImage),
    description:
      get(listing, 'listing_remarks.legal_description') || EMPTY_VALUE,
    brand: {
      '@type': 'Brand',
      name: ADC_DEFAULT_VALUE,
    },
    offers: {
      '@type': 'Offer',
      url: listingPageUrl(listing),
      priceCurrency: 'USD',
      price: propertyPrice(listing),
      availability:
        listingStatusGroup(listing) === enums.ListingStatusGroup.Sold
          ? 'OutOfStock'
          : 'InStock',
    },
  }
}
/**
 * @name singleFamilyHomeSchema
 * @param {*} listing
 * @description Below are the graphQL fields associated to the singleFamilyHomeSchema properties
 * @field {string} primary_property.summary.formatted_address
 * @field {string} primary_property.summary.address.country_primary_subdivision
 * @field {string} primary_property.summary.address.country_secondary_subdivision
 * @field {string} primary_property.summary.address.coordinates.lat
 * @field {string} primary_property.summary.address.coordinates.lon
 * @field {string} listing_page_path
 * @field {string} primary_property.summary.street_address
 * @field {string} primary_property.summary.postal_code
 * @field {string} primary_property.summary.country_code
 * @field {string} primary_property.summary.year_built
 * @field {integer} primary_property.bedrooms
 * @field {integer} primary_property.summary.living_size
 * @field {float} primary_property.bathrooms
 * @field {string} listing_remarks.legal_description
 * @field {string} listing_remarks.mls_comment
 */
const singleFamilyHomeSchema = listing => {
  const {
    state,
    county,
    streetAddress,
    postalCode,
    countryCode,
    formattedAddress,
  } = listingAddress(listing)
  return {
    '@context': 'https://schema.org',
    '@type': 'SingleFamilyResidence',
    name: isArray(formattedAddress)
      ? formattedAddress.join(' ')
      : formattedAddress,
    description: get(listing, 'listing_remarks.mls_comment') || EMPTY_VALUE,
    numberOfRooms: get(listing, 'primary_property.summary.total_bedrooms') || 0,
    floorSize: {
      '@type': 'QuantitativeValue',
      value:
        get(listing, 'primary_property.summary.square_footage') ||
        get(listing, 'primary_property.summary.living_size') ||
        0,
      unitCode: 'FTK',
    },
    numberOfBathroomsTotal:
      get(listing, 'primary_property.summary.total_bathrooms') || 0,
    numberOfBedrooms:
      get(listing, 'primary_property.summary.total_bedrooms') || 0,
    permittedUsage:
      get(listing, 'listing_remarks.legal_description') || EMPTY_VALUE,
    yearBuilt:
      get(listing, 'primary_property.summary.year_built') ||
      new Date().getYear(),
    address: {
      '@type': 'PostalAddress',
      addressCountry: countryCode,
      addressLocality: county,
      addressRegion: state,
      postalCode: postalCode,
      streetAddress,
    },
    url: listingPageUrl(listing) || 'https://www.auction.com',
    ...(get(listing, 'primary_property.summary.address.coordinates.lat') &&
    get(listing, 'primary_property.summary.address.coordinates.lon')
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: get(
              listing,
              'primary_property.summary.address.coordinates.lat'
            ),
            longitude: get(
              listing,
              'primary_property.summary.address.coordinates.lon'
            ),
          },
        }
      : {}),
  }
}

const corporateSchema = logoImage => ({
  '@type': 'Corporation',
  name: ADC_DEFAULT_VALUE,
  url: ADC_URL,
  address: ADC_POSTAL_ADDRESS_SCHEMA,
  location: ADC_POSTAL_ADDRESS_SCHEMA,
  logo: {
    '@type': 'ImageObject',
    inLanguage: 'en-US',
    '@id': `${ADC_URL}/#logo`,
    contentUrl: logoImage,
    url: logoImage,
    caption: ADC_DEFAULT_VALUE,
  },
  sameAs: [
    'https://www.facebook.com/Auctioncom',
    'https://www.youtube.com/@auctionsnow',
    'https://www.linkedin.com/company/auction',
    'https://twitter.com/auction',
    'https://www.instagram.com/Auction_Com/',
    'https://www.auction.com/lp/about-us/',
    'https://www.auction.com/lp/blog/',
  ],
})

const searchSiteSchema = (keywords = []) => {
  return {
    '@type': 'WebSite',
    '@id': `${ADC_URL}/#website`,
    url: ADC_URL,
    name: ADC_DEFAULT_VALUE,
    description:
      'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
    publisher: {
      '@id': `${ADC_URL}/#organization`,
    },
    alternateName: ADC_DEFAULT_VALUE,
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${ADC_URL}/residential/{search_term_string}_qs`,
        },
        'query-input': 'required name=search_term_string',
      },
    ],
    inLanguage: 'en-US',
    sameAs: [
      'https://www.facebook.com/Auctioncom',
      'https://www.youtube.com/@auctionsnow',
      'https://www.linkedin.com/company/auction',
      'https://twitter.com/auction',
      'https://www.instagram.com/Auction_Com/',
      'https://www.auction.com/lp/about-us/',
      'https://www.auction.com/lp/blog/',
    ],
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords)),
  }
}

const organizationSchema = logoImage => {
  return {
    '@type': 'Organization',
    '@id': `${ADC_URL}/#organization`,
    name: ADC_DEFAULT_VALUE,
    description:
      'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
    url: ADC_URL,
    alternateName: ADC_DEFAULT_VALUE,
    logo: {
      '@type': 'ImageObject',
      inLanguage: 'en-US',
      '@id': `${ADC_URL}/#logo`,
      contentUrl: logoImage,
      url: logoImage,
      caption: ADC_DEFAULT_VALUE,
    },
    image: {
      '@id': `${ADC_URL}/#logo`,
    },
    sameAs: [
      'https://www.facebook.com/Auctioncom',
      'https://www.youtube.com/@auctionsnow',
      'https://www.linkedin.com/company/auction',
      'https://twitter.com/auction',
      'https://www.instagram.com/Auction_Com/',
      'https://www.auction.com/lp/about-us/',
      'https://www.auction.com/lp/blog/',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '(800) 793-6107',
      contactType: 'Customer Support',
    },
  }
}
const defaultBreadCrumb = {
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@id': `${ADC_URL}`,
        name: `${ADC_DEFAULT_VALUE}`,
      },
    },
  ],
}
const webPageSchema = (
  url = ADC_URL,
  name = ADC_DEFAULT_VALUE,
  description = 'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
  breadcrumb = defaultBreadCrumb,
  keywords = []
) => {
  return {
    '@type': 'WebPage',
    '@id': url,
    url: url,
    name: name,
    isPartOf: {
      '@id': `${ADC_URL}/#website`,
    },
    description: description,
    breadcrumb: breadcrumb,
    inLanguage: 'en-US',
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: [url],
      },
    ],
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords)),
  }
}

const faqPageSchema = (
  url = ADC_URL,
  name = ADC_DEFAULT_VALUE,
  description = 'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
  breadcrumb = defaultBreadCrumb,
  qaList = DEFAULT_ADC_QA,
  keywords = []
) => {
  const qaSchema = qaList.map(qa => {
    return {
      '@type': 'Question',
      name: qa.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: qa.answer,
      },
    }
  })

  return {
    '@type': 'FAQPage',
    '@id': url,
    url: url,
    name: name,
    isPartOf: {
      '@id': `${ADC_URL}/#website`,
    },
    description: description,
    breadcrumb: breadcrumb,
    inLanguage: 'en-US',
    mainEntity: qaSchema,
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords)),
  }
}

module.exports = {
  breadCrumbSchema,
  eventSchema,
  productSchema,
  singleFamilyHomeSchema,
  corporateSchema,
  searchSiteSchema,
  organizationSchema,
  webPageSchema,
  faqPageSchema,
}
